import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { registerUser } from '../../api';
import { paths } from '../../app/paths';
import { Button, InlineAlert, TextField, Wordmark } from '../../ui';
import { useAuth } from './AuthProvider';
import { formErrorsFrom } from './serverErrors';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 8;

type FieldKey = 'username' | 'password' | 'confirm' | 'firstName' | 'lastName' | 'email' | 'mobile';
type Errors = Partial<Record<FieldKey, string>>;
const FIELDS: FieldKey[] = ['username', 'password', 'confirm', 'firstName', 'lastName', 'email', 'mobile'];
const INITIAL: Record<FieldKey, string> = { username: '', password: '', confirm: '', firstName: '', lastName: '', email: '', mobile: '' };

/** The same rules the server enforces, so most mistakes are caught before anything is sent. */
export function validateRegistration(v: Record<FieldKey, string>): Errors {
  const errors: Errors = {};
  const username = v.username.trim();
  if (!username) errors.username = 'Choose a username.';
  else if (/\s/.test(username)) errors.username = 'A username cannot contain spaces.';
  if (!v.password) errors.password = 'Choose a password.';
  else if (v.password.length < MIN_PASSWORD) errors.password = `A password needs at least ${MIN_PASSWORD} characters.`;
  if (v.password && v.confirm !== v.password) errors.confirm = 'The two passwords do not match.';
  if (v.email.trim() && !EMAIL_PATTERN.test(v.email.trim())) errors.email = 'Enter a valid email address.';
  return errors;
}

/**
 * "Create account" (POST /login/register): signing up creates a NEW ORGANISATION (a generated client id) with this person as
 * its admin, all set by the server. The form sends only the fields it shows (never `role` or `clientId`).
 * The server's 400 `errors` ("password: must be at least 8 characters") are shown under the matching field, a 409 under the
 * username, and a 403 ("Registration is switched off") as a message. On success the user goes back to the sign-in
 * screen with the username filled in: the register response carries no token, so signing in stays a separate step.
 */
export function RegisterPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) return <Navigate to={paths.projects()} replace />;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as FieldKey;
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // guards against a double submit (Enter key + click)
    const next = validateRegistration(form);
    setErrors(next);
    setFormError('');
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const user = await registerUser(form);
      navigate(paths.login(), { replace: true, state: { registered: (user && user.username) || form.username.trim() } });
    } catch (err) {
      // 409 -> under the username; a 400's lines -> under their fields; the rest (offline, 403 "Registration is switched off") -> a message
      const { fieldErrors, general } = formErrorsFrom(err, FIELDS);
      setErrors(fieldErrors as Errors);
      if (general) setFormError(general);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center"><Wordmark className="text-2xl" /></div>
        <form onSubmit={onSubmit} noValidate className="rounded-card border border-line bg-surface p-6 shadow-card">
          <h1 className="text-xl font-bold tracking-tight text-ink">Create account</h1>
          <p className="mb-5 mt-1 text-sm text-muted">Signing up creates a new organisation with you as its admin. To join an existing one, ask its admin to add you. You can sign in straight afterwards.</p>
          <div className="space-y-4">
            <TextField label="Username" name="username" autoComplete="username" autoFocus required value={form.username} onChange={onChange} error={errors.username} hint="No spaces. It must be unique, whatever the capital letters." />
            <TextField label="Password" name="password" type="password" autoComplete="new-password" required value={form.password} onChange={onChange} error={errors.password} hint={`At least ${MIN_PASSWORD} characters.`} />
            <TextField label="Confirm password" name="confirm" type="password" autoComplete="new-password" required value={form.confirm} onChange={onChange} error={errors.confirm} />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField label="First name" name="firstName" autoComplete="given-name" maxLength={80} value={form.firstName} onChange={onChange} error={errors.firstName} />
              <TextField label="Last name" name="lastName" autoComplete="family-name" maxLength={80} value={form.lastName} onChange={onChange} error={errors.lastName} />
            </div>
            <TextField label="Email" name="email" type="email" autoComplete="email" maxLength={160} value={form.email} onChange={onChange} error={errors.email} />
            <TextField label="Mobile" name="mobile" type="tel" autoComplete="tel" maxLength={40} value={form.mobile} onChange={onChange} error={errors.mobile} />
            {formError && <InlineAlert>{formError}</InlineAlert>}
            <Button type="submit" loading={submitting} className="w-full">Create account</Button>
          </div>
          <p className="mt-5 text-center text-sm text-muted">Already have an account? <Link to={paths.login()} className="font-semibold text-brand-700 hover:underline">Sign in</Link></p>
        </form>
      </div>
    </main>
  );
}
